import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import { CiCirclePlus, CiCircleMinus } from "react-icons/ci";
import "./CreateInvoicePage.css";

const CreateInvoicePage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [invoiceType, setInvoiceType] = useState("one-time");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const vendorRes = await api.get(`/business/${businessId}/vendors`);
        setVendors(vendorRes.data);

        const productRes = await api.get(`/business/${businessId}/products`);
        setProducts(productRes.data);

        const businessRes = await api.get(`/businesses/${businessId}`);
        if (businessRes.data.sales_tax_rate) {
          setTaxRate(parseFloat(businessRes.data.sales_tax_rate));
        }
      } catch (err) {
        console.error("Error fetching setup data:", err);
      }
    };

    fetchData();
  }, [businessId]);

  const handleAddEntry = () => {
    setEntries((prev) => [
      ...prev,
      {
        id: Date.now(),
        date: "",
        productId: "",
        manual: false,
        description: "",
        type: "expense",
        price: "",
        quantity: "",
        amount: "",
        amountManuallySet: false,
      },
    ]);
  };

  const handleEntryChange = (idx, field, value) => {
  const newEntries = [...entries];
  const entry = newEntries[idx];

  entry[field] = value;

  const isManual = entry.manual;



   if (field === "manual") {
   entry.price = "";
   entry.quantity = "";
   entry.amount = "";
   entry.amountManuallySet = false;
 }


  // Track manual override of amount
  if (field === "amount") {
    entry.amount = value;
    entry.amountManuallySet = true;
  }

  // Reset manual override if switching between Product/Manual or changing product
  if (field === "manual" || field === "productId") {
    entry.amountManuallySet = false;
  }

  // Auto-calculate for manual entries if amount wasn't overridden
  if (isManual && (field === "price" || field === "quantity")) {
    const price = parseFloat(entry.price || 0);
    const quantity = parseFloat(entry.quantity || 0);
    if (!isNaN(price) && !isNaN(quantity) && !entry.amountManuallySet) {
      entry.amount = (price * quantity).toFixed(2);
    }
  }

  // Auto-calculate for product entries
  if (!isManual && (field === "productId" || field === "quantity")) {
    const product = products.find(p => p.id === Number(entry.productId));
    const quantity = parseFloat(entry.quantity || 0);
    if (product && quantity) {
      entry.price = product.price;
      entry.amount = (product.price * quantity).toFixed(2);
      entry.amountManuallySet = false; // always recalc for product entries
    }
  }

  setEntries(newEntries);
};


  const handleSubmit = async () => {
  if (!selectedVendor) {
    alert("Vendor is required.");
    return;
  }

  if (invoiceType === "one-time" && !invoiceDate) {
    alert("Invoice date is required for one-time invoices.");
    return;
  }

  if (entries.length === 0) {
    alert("At least one entry is required.");
    return;
  }

  const processedEntries = entries.map((e) => {
    const isManual = e.manual;
    const quantity = parseFloat(e.quantity || 1);
    const price = parseFloat(
      isManual
        ? e.price
        : (() => {
            const prod = products.find((p) => p.id === Number(e.productId));
            return prod ? prod.price : 0;
          })()
    );

    const rawAmount = parseFloat(e.amount || 0);
    let amount = rawAmount;

    if (isManual && e.type === "income") {
      amount = -Math.abs(rawAmount); // Flip sign only for manual income
    }

    let salesTax = 0;
    if (!isManual) {
      const product = products.find((p) => p.id === Number(e.productId));
      const baseAmount = price * quantity;
      if (product?.taxable && taxRate) {
        salesTax = parseFloat((baseAmount * taxRate) / 100).toFixed(2);
      }
    }

    const product = products.find((p) => p.id === Number(e.productId));

    return {
      entry_date:
        invoiceType === "open" && e.date
          ? format(new Date(e.date), "yyyy-MM-dd")
          : null,
      product_id: isManual ? null : e.productId,
      is_manual: isManual,
      description: isManual ? e.description : product?.item || null,
      type: e.type,
      quantity: isNaN(quantity) ? null : quantity,
      price: isNaN(price) ? null : price,
      amount: isNaN(amount) ? 0 : amount,
      sales_tax: isNaN(salesTax) ? 0 : parseFloat(salesTax),
    };
  });

  try {
    await api.post(`/business/${businessId}/invoices`, {
      vendor_id: selectedVendor,
      invoice_type: invoiceType,
      invoice_date: invoiceType === "one-time" ? invoiceDate : null,
      entries: processedEntries,
    });

    alert("Invoice submitted successfully.");
    navigate(`/dashboard/business/${businessId}/invoiceref`);
  } catch (err) {
    console.error("Failed to submit invoice:", err);
    alert("Error submitting invoice.");
  }
};


  return (
    <div className="create-invoice-container">
      <h2>Create New Invoice</h2>

      <div className="invoice-top-row">
        <select value={selectedVendor} onChange={(e) => setSelectedVendor(e.target.value)}>
          <option value="">Select Customer</option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>

        <select value={invoiceType} onChange={(e) => setInvoiceType(e.target.value)}>
          <option value="one-time">One Time</option>
          <option value="open">Open</option>
        </select>

        {invoiceType === "one-time" && (
          <label className="first-date">
          <p>Date</p>
          <input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
          />
          </label>
        )}
      </div>

      {entries.map((entry, idx) => (
        <div className="invoice-entry-box" key={entry.id}>

        <div className="entry-type-wrapper">
  
  <div className="entry-mode-switch">
    <button
      type="button"
      className={entry.manual ? '' : 'active'}
      onClick={() => handleEntryChange(idx, "manual", false)}
    >
      Product
    </button>
    <button
      type="button"
      className={entry.manual ? 'active' : ''}
      onClick={() => handleEntryChange(idx, "manual", true)}
    >
      Manual
    </button>
  </div>
</div>

          {invoiceType === "open" && (
            <>
            <div className="invoice-date-entry">
            <label>Date</label>
            <input
              type="date"
              value={entry.date || ""}
              onChange={(e) => handleEntryChange(idx, "date", e.target.value)}
              placeholder="Entry Date"
              className="entry-date-input"
            />

            </div>

  </>
          )}

          

          {entry.manual ? (
            <>
              <input
                type="text"
                placeholder="Description"
                value={entry.description}
                onChange={(e) => handleEntryChange(idx, "description", e.target.value)}
              />
              <select
              className="entry-type"
                value={entry.type}
                onChange={(e) => handleEntryChange(idx, "type", e.target.value)}
              >
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            
              <input
              className="small-input"
                type="number"
                placeholder="Price"
                value={entry.price}
                onChange={(e) => handleEntryChange(idx, "price", e.target.value)}
              />
              <input
              className="small-input"
                type="number"
                placeholder="Qty"
                value={entry.quantity}
                onChange={(e) => handleEntryChange(idx, "quantity", e.target.value)}
              />
              <input
                type="number"
                placeholder="Amount"
                value={entry.amount}
                onChange={(e) => handleEntryChange(idx, "amount", e.target.value)}
              />
              
            </>
          ) : (
            <>
              <select
                value={entry.productId}
                onChange={(e) => handleEntryChange(idx, "productId", e.target.value)}
              >
                <option value="">Select Product</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.item}</option>
                ))}
              </select>
              <input
                type="number"
                placeholder="Qty"
                value={entry.quantity}
                onChange={(e) => handleEntryChange(idx, "quantity", e.target.value)}
              />
              <input
                type="number"
                placeholder="Amount"
                value={entry.amount}
                readOnly
              />
            </>
          )}
        </div>
      ))}

      <div className="entry-control-row">
        <button onClick={handleAddEntry}><CiCirclePlus /> Add Entry</button>
        {entries.length > 0 && (
          <button onClick={() => setEntries((prev) => prev.slice(0, -1))}><CiCircleMinus /> Remove Entry</button>
        )}
      </div>

      <div className="submit-control-row">
        <button className="submit-invoice" onClick={handleSubmit}>Submit Invoice</button>
        <button className="cancel-invoice" onClick={() => navigate(-1)}>Cancel</button>
      </div>
    </div>
  );
};

export default CreateInvoicePage;

